import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Pagination } from '@mui/material';
import Button from "@mui/material/Button";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Dropdown from "react-bootstrap/Dropdown";
import { v4 as uuidv4 } from "uuid";

import NavBar from "../../structure/NavBar";
import { NavBarTop } from "../../structure/NavBarTop";
import NoDataImg from "../../../assets/img/no-data.gif";
import {
  updateTopHeader,
  updateLoading,
} from "../../../redux/features/common/commonSlice";
import urls from "../../constants/apiurl";
import ApiCaller from "../../constants/ApiCaller";
import { showDangerToast, showToast } from "../../constants/toaster";
import Utils from "../../constants/Utils";
import InsufficientPermission from "../../custom/InSufficientPermission";

export const ProductDiscountList = () => {
  const { formatDate, formatDateStable } = Utils();
  const [callAPI, setCallAPI] = useState("");
  const [searchMerchant, setSearchMerchant] = useState("");
  const [showMerchantDropdown, setShowMerchantDropdown] = useState(false);
  const [filteredMerchants, setFilteredMerchants] = useState([]);
  const [discountPercentage, setDiscountPercentage] = useState("");
  const [merchantDropdownText, setMerchantDropdownText] = useState("Select Merchant");
  const [merchantId, setMerchantId] = useState("");
  const dispatch = useDispatch();
  const [datalist, setDatalist] = useState([]);
  const [merchantList, setMerchantList] = useState([]);
  const [sort_type] = useState("createdAt");
  const [sort_order] = useState(false);
  const userData = useSelector((state) => state?.userData);
  const [page, setPage] = useState(1);
  const userPermissions = useSelector((state) => state?.permission);

  const [pagerecord, setPagerecord] = useState({
    totalItems: 0,
    pageNumber: 1,
    pageSize: 10,
    totalPages: 1,
  });

  // Permission checks
  let currentModulePermission;
  let viewPermission = true;

  if (userPermissions?.length > 0) {
    currentModulePermission = userPermissions?.find(
      (permission) => permission?.name === "PRODUCT_DISCOUNT"
    );
    if (currentModulePermission) {
      viewPermission = currentModulePermission?.view;
    }
  }

  useEffect(() => {
    dispatch(updateTopHeader("Product Discount List"));
  }, [dispatch]);

  useEffect(() => {
    if (viewPermission) {
      getProductDiscountList();
      getMerchantList();
    }
  }, [callAPI, viewPermission]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMerchantDropdown && !event.target.closest('.merchant-dropdown-container')) {
        setShowMerchantDropdown(false);
        setSearchMerchant("");
        setFilteredMerchants(merchantList);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMerchantDropdown, merchantList]);

  const getProductDiscountList = async () => {
    try {
      dispatch(updateLoading(true));

      const params = {
        page: page - 1 || 0,
        size: 10,
        discountPercentage: discountPercentage,
        merchantId: merchantId,
      };

      let path = `${urls.productDiscountList}?page=${params.page}&size=${params.size}&discountPercentage=${params.discountPercentage}&merchantId=${params.merchantId}`;
      
      console.log("API Path:", path);
      let res = await ApiCaller.get({}, path, userData?.access_token);
      const response = await res.response.json();

      if (response.status === 200 || response.status === "200") {
        dispatch(updateLoading(false));
        setDatalist(response?.data?.items || []);
        setPagerecord({
          totalItems: response?.data?.totalItems || 0,
          pageNumber: (response?.data?.pageNumber || 0) + 1,
          pageSize: response?.data?.pageSize || 10,
          totalPages: response?.data?.totalPages || 1,
        });
      } else {
        dispatch(updateLoading(false));
        showDangerToast(
          response?.message || "Failed to fetch product discounts"
        );
        setDatalist([]);
      }
    } catch (error) {
      dispatch(updateLoading(false));
      console.error("Error fetching product discounts:", error);
      showDangerToast("An unexpected error occurred while fetching data");
      setDatalist([]);
    }
  };

  const getMerchantList = async () => {
    try {
      let path = urls.businessMerchantList;
      const requestBody = {
        searchTxt: "",
        page: 0,
        limit: 100, // Get more merchants for dropdown
        sort: "createdAt",
        active: null,
        order: false
      };
      
      let res = await ApiCaller.post(requestBody, path, userData?.access_token);
      const response = await res.response.json();

      if (response.status === 200 || response.status === "200") {
        const merchants = response?.data?.items || [];
        setMerchantList(merchants);
        setFilteredMerchants(merchants);
      } else {
        console.error("Failed to fetch merchant list");
      }
    } catch (error) {
      console.error("Error fetching merchant list:", error);
    }
  };

  const handleMerchantSelect = (merchant) => {
    setMerchantId(merchant.uuid || merchant.id || "");
    setMerchantDropdownText(merchant.businessName || merchant.name || "Select Merchant");
    setShowMerchantDropdown(false);
    setSearchMerchant("");
    setFilteredMerchants(merchantList);
  };

  const handleMerchantSearch = (e) => {
    const searchValue = e.target.value.toLowerCase();
    setSearchMerchant(searchValue);
    
    if (searchValue === "") {
      setFilteredMerchants(merchantList);
    } else {
      const filtered = merchantList.filter(merchant =>
        (merchant.businessName || "").toLowerCase().includes(searchValue) ||
        (merchant.email || "").toLowerCase().includes(searchValue)
      );
      setFilteredMerchants(filtered);
    }
  };

  const toggleMerchantDropdown = () => {
    setShowMerchantDropdown(!showMerchantDropdown);
    if (!showMerchantDropdown) {
      setSearchMerchant("");
      setFilteredMerchants(merchantList);
    }
  };

  const handleDiscountPercentageChange = (e) => {
    setDiscountPercentage(e.target.value);
  };

  const handleFilterClick = useCallback(() => {
    setPage(1);
    setPagerecord({
      ...pagerecord,
      pageNumber: 1,
    });
    setCallAPI(uuidv4());
  }, [discountPercentage, merchantId]);

  const handleResetFilter = () => {
    setDiscountPercentage("");
    setMerchantId("");
    setMerchantDropdownText("Select Merchant");
    setSearchMerchant("");
    setShowMerchantDropdown(false);
    setFilteredMerchants(merchantList);
    setPage(1);
    setPagerecord({
      totalItems: 0,
      pageNumber: 1,
      pageSize: 10,
      totalPages: 1,
    });
    setCallAPI(uuidv4());
  };

  const handleChange = (event, value) => {
    if (typeof value !== "number" || value < 1) return;
    setPage(value);
    setPagerecord({
      totalItems: pagerecord?.totalItems,
      totalPages: pagerecord?.totalPages,
      pageNumber: value,
      pageSize: 10,
    });
    setCallAPI(uuidv4());
  };

  if (!viewPermission) {
    return <InsufficientPermission />;
  }

  return (
    <>
      <NavBar />
      <div className="main-content" id="panel">
        <NavBarTop />
        <div className="header bg-primary pb-6">
          <div className="container-fluid">
            <div className="header-body">
              <div className="row align-items-center py-4">
                <div className="col-lg-6 col-7">
                  <h6 className="h2 text-white d-inline-block mb-0">
                    Product Discount List
                  </h6>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid mt--6">
          <div className="row">
            <div className="col">
              <div className="card">
                <div className="card-header border-0">
                  <div className="row align-items-center">
                    <div className="col">
                      <h3 className="mb-0">Product Discounts</h3>
                    </div>
                  </div>
                </div>

                {/* Filters */}
                <div className="card-header border-0">
                  <div className="row">
                    <div className="col-lg-3 col-md-6 mb-3">
                      <Form.Label>Merchant</Form.Label>
                      <div className="position-relative merchant-dropdown-container">
                        <InputGroup>
                          <Form.Control
                            type="text"
                            placeholder="Search and select merchant"
                            value={showMerchantDropdown ? searchMerchant : merchantDropdownText}
                            onChange={handleMerchantSearch}
                            onFocus={toggleMerchantDropdown}
                            style={{ 
                              cursor: showMerchantDropdown ? "text" : "pointer",
                              backgroundColor: showMerchantDropdown ? "white" : "#f8f9fa"
                            }}
                            readOnly={!showMerchantDropdown}
                          />
                          <InputGroup.Text 
                            className="cursor-pointer"
                            onClick={toggleMerchantDropdown}
                            style={{ 
                              backgroundColor: "#e9ecef",
                              borderLeft: "1px solid #ced4da"
                            }}
                          >
                            <i className={`fas fa-chevron-${showMerchantDropdown ? 'up' : 'down'}`}></i>
                          </InputGroup.Text>
                        </InputGroup>
                        
                        {showMerchantDropdown && (
                          <div 
                            className="position-absolute w-100 bg-white border rounded shadow-lg mt-1"
                            style={{ 
                              zIndex: 1050, 
                              maxHeight: "200px", 
                              overflowY: "auto",
                              border: "1px solid #dee2e6"
                            }}
                          >
                            <div 
                              className="px-3 py-2 border-bottom cursor-pointer hover-bg-light"
                              onClick={() => handleMerchantSelect({ uuid: "", businessName: "All Merchants" })}
                              style={{ backgroundColor: "#f8f9fa" }}
                            >
                              <strong>All Merchants</strong>
                            </div>
                            {filteredMerchants.length > 0 ? (
                              filteredMerchants.map((merchant) => (
                                <div
                                  key={merchant.uuid}
                                  className="px-3 py-2 cursor-pointer border-bottom"
                                  onClick={() => handleMerchantSelect(merchant)}
                                  style={{ 
                                    cursor: "pointer",
                                    transition: "background-color 0.2s"
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = "#f8f9fa"}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = "white"}
                                >
                                  <div className="d-flex align-items-center">
                                    {merchant.businessLogo && (
                                      <img
                                        src={merchant.businessLogo}
                                        alt={merchant.businessName}
                                        className="me-2 rounded"
                                        style={{ width: "30px", height: "30px", objectFit: "cover" }}
                                      />
                                    )}
                                    <div>
                                      <div className="fw-bold">{merchant.businessName}</div>
                                      <small className="text-muted">{merchant.email}</small>
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-muted">
                                No merchants found
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-lg-3 col-md-6 mb-3">
                      <Form.Label>Discount Percentage</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          placeholder="Enter discount %"
                          value={discountPercentage}
                          onChange={handleDiscountPercentageChange}
                          min="0"
                          max="100"
                        />
                      </InputGroup>
                    </div>

                    <div className="col-lg-6 col-md-12 mb-3 d-flex align-items-end">
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleFilterClick}
                        className="me-2"
                      >
                        Search
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleResetFilter}
                      >
                        Reset
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Table */}
                <div className="table-responsive">
                  <table className="table align-items-center table-flush">
                    <thead className="thead-light">
                      <tr>
                        <th scope="col" className="sort">
                          S.No
                        </th>
                        <th scope="col" className="sort">
                          Product Name
                        </th>
                        <th scope="col" className="sort">
                          Merchant
                        </th>
                        <th scope="col" className="sort">
                          Discount %
                        </th>
                        <th scope="col" className="sort">
                          Start Date
                        </th>
                        <th scope="col" className="sort">
                          End Date
                        </th>
                      </tr>
                    </thead>
                    <tbody className="list">
                      {datalist.length > 0 ? (
                        datalist.map((item, index) => (
                          <tr key={item.id || index}>
                            <td className="budget">
                              {(pagerecord.pageNumber - 1) * pagerecord.pageSize + index + 1}
                            </td>
                            <td className="budget">
                              <span className="badge badge-dot mr-4">
                                <span className="status">
                                  {item.productName || "N/A"}
                                </span>
                              </span>
                            </td>
                            <td className="budget">
                              <span className="badge badge-dot mr-4">
                                <span className="status">
                                  {item.merchantName || "N/A"}
                                </span>
                              </span>
                            </td>
                            <td className="budget">
                              <span className="badge badge-dot mr-4">
                                <span className="status">
                                  {item.discountPercentage || 0}%
                                </span>
                              </span>
                            </td>
                            <td className="budget">
                              <span className="badge badge-dot mr-4">
                                <span className="status">
                                  {item.startDate ? formatDate(item.startDate) : "N/A"}
                                </span>
                              </span>
                            </td>
                            <td className="budget">
                              <span className="badge badge-dot mr-4">
                                <span className="status">
                                  {item.endDate ? formatDate(item.endDate) : "N/A"}
                                </span>
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center">
                            <img
                              src={NoDataImg}
                              alt="No data"
                              style={{ width: "200px", height: "200px" }}
                            />
                            <br />
                            <span>No product discounts found</span>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagerecord.totalPages > 1 && (
                  <div className="card-footer py-4">
                    <nav aria-label="...">
                      <Box display="flex" justifyContent="center">
                        <Pagination
                          count={pagerecord.totalPages}
                          page={pagerecord.pageNumber}
                          onChange={handleChange}
                          variant="outlined"
                          shape="rounded"
                          color="primary"
                        />
                      </Box>
                    </nav>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductDiscountList;